import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveAppointment, checkSlotAvailability } from '../../services/appointmentService';
import { createPaymentIntent, updatePaymentStatus } from '../../services/stripeService';
import siteSettingsService from '../../services/siteSettingsService';
import { Timestamp } from 'firebase/firestore';
import { loadStripe } from '@stripe/stripe-js';

import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';

// Determine which Stripe publishable key to use based on environment
const isDevMode = process.env.NODE_ENV === 'development';
const stripePublishableKey = isDevMode
  ? process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY_DEV_MODE
  : process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

// Initialize Stripe with the appropriate publishable key
// Log the key being used (for debugging purposes)
console.log(`Stripe Key (${isDevMode ? 'DEV' : 'PROD'}):`, stripePublishableKey);
const stripePromise = loadStripe(stripePublishableKey);


// Fixed price for dental emergency appointment (£50)
const APPOINTMENT_PRICE = 3900; // in pence (50 GBP)

/**
 * Checkout form component that handles the Stripe payment form
 */
const CheckoutForm = ({ 
  appointmentData, 
  email, 
  practice,
  onSuccess, 
  onError,
  useImmediatePaymentProcessing,
  navigateToConfirmation,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [clientSecret, setClientSecret] = useState('');
  const [appointmentId, setAppointmentId] = useState('');

  // Process the payment when the form is submitted
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet
      return;
    }

    setIsProcessing(true);
    setPaymentError(null);

    try {
      // Check if the slot is already booked before proceeding
      // This helps prevent double-booking due to race conditions
      const isSlotAvailable = await checkSlotAvailability(
        appointmentData.slot.availabilityId,
        appointmentData.slot.slotIndex
      );
      
      if (!isSlotAvailable) {
        setPaymentError("Sorry, this appointment slot has just been booked by someone else. Please select another time.");
        setIsProcessing(false);
        onError("This slot is no longer available");
        return;
      }
      
      // Create the appointment in Firestore first
      const newAppointmentId = await saveAppointment({
        ...appointmentData,
        userInfo: {
          ...appointmentData.userInfo,
          email: email
        },
        paymentStatus: 'pending',
        status: 'pending',
        createdAt: Timestamp.now(),
        // Add practice address and phone for email template
        practiceAddress: practice.address ? 
          `${practice.address.line1}${practice.address.line2 ? `, ${practice.address.line2}` : ''}, ${practice.address.city}, ${practice.address.postcode}` : 
          'Address not available',
        practicePhone: practice.phone || 'Not available'
      });

      setAppointmentId(newAppointmentId);

      // Create a payment intent
      const { clientSecret } = await createPaymentIntent(
        appointmentData,
        newAppointmentId,
        APPOINTMENT_PRICE,

      );

      setClientSecret(clientSecret);

      // Confirm the card payment
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: appointmentData.userInfo.name,
            email: email,
          },
        },
      });

      if (error) {
        // Show error to your customer
        setPaymentError(error.message);
        await updatePaymentStatus(newAppointmentId, 'failed', error.message);
        onError(error.message);
      } else if (paymentIntent.status === 'requires_capture') {
        // Payment succeeded, but is only authorized (not captured yet)
        // The payment will be captured when the practice confirms the appointment
        await updatePaymentStatus(newAppointmentId, 'authorized');
        navigateToConfirmation();
      } else if (paymentIntent.status === 'succeeded') {
        // Payment was processed immediately and succeeded
        await updatePaymentStatus(newAppointmentId, 'completed');
        navigateToConfirmation();
      } else {
        // Handle other payment intent statuses
        console.log(`Payment intent status: ${paymentIntent.status}`);
        await updatePaymentStatus(newAppointmentId, 'processing');
        navigateToConfirmation();
      }
    } catch (err) {
      console.error('Error processing payment:', err);
      setPaymentError('There was an error processing your payment. Please try again.');
      
      if (appointmentId) {
        await updatePaymentStatus(appointmentId, 'failed', err.message);
      }
      
      onError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Card Details
        </label>
        <div className="p-3 border border-gray-300 rounded-md">
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
                invalid: {
                  color: '#9e2146',
                },
              },
              hidePostalCode: true // Hide the ZIP/postal code field
            }}
          />
        </div>
      </div>
      
      {paymentError && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">
          {paymentError}
        </div>
      )}
      
      <div className="flex justify-between">
        <button
          type="button"
          onClick={() => onError(null)}
          className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          disabled={isProcessing}
        >
          Back
        </button>
        <button
          type="submit"
          disabled={!stripe || isProcessing}
          className="px-4 py-2 bg-accent hover:bg-accent/90 text-white rounded-md transition-colors"
        >
          {isProcessing ? 'Processing...' : `Pay £${APPOINTMENT_PRICE / 100}`}
        </button>
      </div>
    </form>
  );
};

/**
 * Component to handle payment processing
 */
const PaymentProcessing = ({ 
  formData, 
  practice, 
  selectedSlot, 
  selectedDate, 
  onSuccess, 
  onBack,
  nextAvailableDates
}) => {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [email, setEmail] = useState(formData.email || '');
  const [emailError, setEmailError] = useState('');
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [useImmediatePaymentProcessing, setUseImmediatePaymentProcessing] = useState(false);
  
  // Fetch payment processing setting on component mount
  useEffect(() => {
    const fetchPaymentProcessingSetting = async () => {
      try {
        const settings = await siteSettingsService.getSettings();
        setUseImmediatePaymentProcessing(settings.useImmediatePaymentProcessing || false);
      } catch (error) {
        console.error('Error fetching payment processing setting:', error);
      }
    };
    
    fetchPaymentProcessingSetting();
  }, []);
  


  // Validate email format
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  // Prepare appointment data
  const appointmentData = {
    practiceId: practice.id,
    practiceName: practice.name,
    date: selectedDate,
    slot: {
      startTime: Timestamp.fromDate(selectedSlot.startTime.toDate()),
      endTime: Timestamp.fromDate(selectedSlot.endTime.toDate()),
      operativeId: selectedSlot.operativeId,
      availabilityId: selectedSlot.availabilityId,
      slotIndex: selectedSlot.slotIndex
    },
    userInfo: {
      name: formData.name,
      phone: formData.phone,
      postcode: formData.postcode,
      issue: formData.issue || ''
    }
  };

  // Handle proceeding to payment
  const handleProceedToPayment = () => {
    // Validate email
    if (!email) {
      setEmailError('Email is required to confirm your appointment');
      return;
    }

    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setEmailError('');
    setShowPaymentForm(true);
  };

  // Handle payment error
  const handlePaymentError = (errorMessage) => {
    setError(errorMessage);
    setShowPaymentForm(false);
  };
  
  // Navigate to confirmation page
  const navigateToConfirmation = () => {
    // Navigate to the confirmation page with all necessary data
    navigate('/appointment-confirmation', {
      state: {
        formData: {
          ...formData,
          email: email // Include the email from the payment form
        },
        practice,
        selectedSlot,
        nextAvailableDates
      }
    });
    
    // Also call onSuccess to maintain backward compatibility
    if (onSuccess) {
      onSuccess();
    }
  };

  // Format date for display
  const formatDate = (date) => {
    if (!date) return '';
    
    const [year, month, day] = date.split('-').map(Number);
    const dateObj = new Date(year, month - 1, day);
    
    return dateObj.toLocaleDateString('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Format time for display
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    
    const date = timestamp.toDate();
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Payment Information</h2>
      
      <div className="mb-6 p-4 bg-blue-50 rounded-md">
        {useImmediatePaymentProcessing ? (
          <>
            <p className="text-sm text-gray-700 mb-2">
              <strong>Important:</strong> By proceeding, your payment will be processed immediately.
            </p>
            <p className="text-sm text-gray-700 mb-2">
              The payment will be processed as soon as you complete the payment form.
            </p>
            <p className="text-sm text-gray-700">
              You will receive a confirmation email with your appointment details.
            </p>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-700 mb-2">
              <strong>Important:</strong> By proceeding, you authorize us to hold funds in your account.
            </p>
            <p className="text-sm text-gray-700 mb-2">
              The payment will not be processed until your appointment is confirmed by the dental practice.
            </p>
            <p className="text-sm text-gray-700">
              You will receive a confirmation email once your appointment is confirmed.
            </p>
          </>
        )}
      </div>
      
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-2">Appointment Summary</h3>
        <div className="bg-gray-50 p-3 rounded-md">
          <p className="text-sm text-gray-700"><strong>Date:</strong> {formatDate(selectedDate)}</p>
          <p className="text-sm text-gray-700"><strong>Time:</strong> {formatTime(selectedSlot.startTime)} - {formatTime(selectedSlot.endTime)}</p>
          <p className="text-sm text-gray-700"><strong>Practice:</strong> {practice.name}</p>
          <p className="text-sm text-gray-700"><strong>Name:</strong> {formData.name}</p>
          <p className="text-sm text-gray-700"><strong>Phone:</strong> {formData.phone}</p>
          {email && (
            <p className="text-sm text-gray-700"><strong>Email:</strong> {email}</p>
          )}
        </div>
      </div>
      
      {/* Email field */}
      <div className="mb-6">
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
          Email Address <span className="text-red-500">*</span>
        </label>
        <p className="text-sm text-gray-500 mb-2">
          We need your email address to send appointment confirmation and updates.
        </p>
        <input
          type="email"
          id="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (emailError) setEmailError('');
          }}
          className={`w-full px-3 py-2 border ${emailError ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-accent focus:border-accent`}
          placeholder="your.email@example.com"
          required
        />
        {emailError && (
          <p className="mt-1 text-sm text-red-600">{emailError}</p>
        )}
      </div>
       
      {/* Conditional rendering based on whether to show the payment form */}
      {!showPaymentForm ? (
        <>
          <div className="mb-6 p-4 border border-gray-200 rounded-md">
            <div className="flex justify-between items-center mb-4">
              <span className="text-lg font-semibold">Emergency Dental Appointment</span>
              <span className="text-xl font-bold">£{APPOINTMENT_PRICE / 100}</span>
            </div>
            <p className="text-sm text-gray-600">
              {useImmediatePaymentProcessing 
                ? "This payment will be processed immediately upon completion of the payment form."
                : "This payment will be authorized but not charged until your appointment is confirmed by the practice."}
            </p>
          </div>
          
          
      
          
          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">
              {error}
            </div>
          )}
          
          <div className="flex justify-between">
            <button
              onClick={onBack}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Back
            </button>
            <button
              onClick={() => {
                
                handleProceedToPayment();
              }}
              className={`px-4 py-2 bg-accent hover:bg-accent/90 text-white rounded-md transition-colors`}
           
            >
              Proceed to Payment
            </button>
          </div>
        </>
      ) : (
        <Elements stripe={stripePromise}>
          <CheckoutForm
            appointmentData={appointmentData}
            email={email}
            practice={practice}
            onSuccess={onSuccess}
            onError={handlePaymentError}
            useImmediatePaymentProcessing={useImmediatePaymentProcessing}
            navigateToConfirmation={navigateToConfirmation}
        
          />
        </Elements>
      )}
    </div>
  );
};

export default PaymentProcessing;
